import {
  AddConventionRegistrationBundleToShoppingCartDocument,
  AddConventionRegistrationBundleToShoppingCartMutation,
  AddConventionRegistrationBundleToShoppingCartMutationVariables,
  CreateAnonymousShoppingCartDocument,
  CreateAnonymousShoppingCartMutation,
  CreateAnonymousShoppingCartMutationVariables,
  CreateCustomerShoppingCartDocument,
  CreateCustomerShoppingCartMutation,
  CreateCustomerShoppingCartMutationVariables,
} from '@/generated/graphql'
import { fetchData } from '@/lib/fetcher'
import { useShoppingCart } from '@/ShoppingCart/useShoppingCart'
import { useStore } from '@/store'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { isAuthorizationFailure } from '@/errorHandling'
import { useStorefrontMarket } from '@/useStorefrontMarket'
import { regionalBrandDefaultCountryIso } from '@/lib/checkout/regionalStoreDefaults'
import type { RegionalBrandCode } from '@/lib/seo/marketUrls'

const AddConventionUpgradeToShoppingCartDocument = `
  mutation AddConventionUpgradeToShoppingCart(
    $shoppingCartToken: String!
    $registrationOrderItemId: Int!
  ) {
    addConventionUpgradeToShoppingCart(
      shoppingCartToken: $shoppingCartToken
      registrationOrderItemId: $registrationOrderItemId
    )
  }
`

type AddConventionUpgradeToShoppingCartMutation = {
  addConventionUpgradeToShoppingCart: boolean
}
type AddConventionUpgradeToShoppingCartMutationVariables = {
  shoppingCartToken: string
  registrationOrderItemId: number
}

export type ConventionRegistrantInput = {
  name: string
  workshopProductIds: number[]
  upsellProductId: number | null
  upsellOptionId: number | null
  meta: Array<{ key: string; value: string }>
}

type AddConventionBundleArgs = {
  bypassSoldOut?: boolean
  conventionProductId: number
  currencyId: number | null
  brandCode: RegionalBrandCode | null
  registrationOptionId: number | null
  registrants: ConventionRegistrantInput[]
  paymentPlan: { dueDates: string[] } | null
}

export const ORIGINAL_CART_SNAPSHOT_KEY = 'vi-convention-original-cart-snapshot'

export const useConventionRegistration = () => {
  const { cartData } = useShoppingCart()
  const user = useStore(state => state.user)
  const cachedCountry = useStore(state => state.country)
  const setShoppingCartId = useStore(state => state.setShoppingCartId)
  const queryClient = useQueryClient()
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const storefrontMarket = useStorefrontMarket({
    cartCountryIsoCode: cartData?.country?.isoCode,
    cachedCountryIsoCode: cachedCountry,
  })

  const getCountryIsoCode = (brandCode?: RegionalBrandCode | null) => {
    if (brandCode) {
      return regionalBrandDefaultCountryIso(brandCode)
    }

    return storefrontMarket.countryIsoCode
  }

  const snapshotOriginalCart = () => {
    if (!cartData?.token || !(cartData.items?.length > 0)) return

    try {
      sessionStorage.setItem(
        ORIGINAL_CART_SNAPSHOT_KEY,
        JSON.stringify({
          cartId: cartData.token,
          // Minimal snapshot for future restore work. (Current requirement: separate cart for conventions.)
          countryIsoCode: cartData.country?.isoCode || getCountryIsoCode(),
        })
      )
    } catch {
      // best-effort only
    }
  }

  /** Convention registration always uses a fresh isolated convention cart. */
  const ensureConventionCartId = async (args: {
    conventionProductId: number
    currencyId: number | null
    brandCode: RegionalBrandCode | null
  }): Promise<string | null> => {
    // Country is used only for initial cart setup; follow the event brand when
    // convention product meta forces The Session/UK or Magifest/US.
    const countryIsoCode = getCountryIsoCode(args.brandCode)

    const hasItems = (cartData?.items?.length ?? 0) > 0

    if (hasItems) snapshotOriginalCart()

    if (user?.id) {
      try {
        const res = await fetchData<
          CreateCustomerShoppingCartMutation,
          CreateCustomerShoppingCartMutationVariables
        >(CreateCustomerShoppingCartDocument, {
          customerId: user.id,
          countryIsoCode,
          currencyId: args.currencyId ?? null,
          forceNew: true,
          isConventionCart: true,
        })()
        const newCartId = res?.createCustomerShoppingCart ?? null
        setShoppingCartId(newCartId)
        return newCartId
      } catch (error) {
        if (!isAuthorizationFailure(error)) {
          throw error
        }
      }
    }

    const res = await fetchData<
      CreateAnonymousShoppingCartMutation,
      CreateAnonymousShoppingCartMutationVariables
    >(CreateAnonymousShoppingCartDocument, {
      countryIsoCode,
      currencyId: args.currencyId ?? null,
      isConventionCart: true,
    })()
    const newCartId = res?.createAnonymousShoppingCart ?? null
    setShoppingCartId(newCartId)
    return newCartId
  }

  const addConventionRegistrationBundle = async (
    args: AddConventionBundleArgs
  ) => {
    setIsAddingToCart(true)
    try {
      const cartId = await ensureConventionCartId({
        conventionProductId: args.conventionProductId,
        currencyId: args.currencyId,
        brandCode: args.brandCode,
      })
      if (!cartId) throw new Error('Unable to create a shopping cart.')

      const registrants = args.registrants.map(r => ({
        name: r.name,
        workshopProductIds:
          r.workshopProductIds.length > 0 ? r.workshopProductIds : null,
        upsellProductId: r.upsellProductId ?? null,
        upsellOptionId: r.upsellOptionId ?? null,
        meta: r.meta.length > 0 ? r.meta : null,
      }))

      await fetchData<
        AddConventionRegistrationBundleToShoppingCartMutation,
        AddConventionRegistrationBundleToShoppingCartMutationVariables
      >(AddConventionRegistrationBundleToShoppingCartDocument, {
        shoppingCartToken: cartId,
        conventionProductId: args.conventionProductId,
        registrationOptionId: args.registrationOptionId ?? null,
        paymentPlan: args.paymentPlan ?? null,
        bypassSoldOut: args.bypassSoldOut ?? false,
        registrants,
      })()

      // Invalidate cart queries to trigger refetch
      await queryClient.invalidateQueries({
        queryKey: ['ActiveShoppingCart'],
        exact: false,
      })

      return cartId
    } finally {
      setIsAddingToCart(false)
    }
  }

  const addConventionUpgrade = async (args: {
    registrationOrderItemId: number
    conventionProductId: number
    currencyId: number
    brandCode: RegionalBrandCode
  }) => {
    setIsAddingToCart(true)
    try {
      const cartId = await ensureConventionCartId(args)
      if (!cartId) throw new Error('Unable to create a shopping cart.')
      await fetchData<
        AddConventionUpgradeToShoppingCartMutation,
        AddConventionUpgradeToShoppingCartMutationVariables
      >(AddConventionUpgradeToShoppingCartDocument, {
        shoppingCartToken: cartId,
        registrationOrderItemId: args.registrationOrderItemId,
      })()
      await queryClient.invalidateQueries({
        queryKey: ['ActiveShoppingCart'],
        exact: false,
      })
      return cartId
    } finally {
      setIsAddingToCart(false)
    }
  }

  return {
    addConventionRegistrationBundle,
    addConventionUpgrade,
    isPending: isAddingToCart,
  }
}

/**
 * Utility to clear the original cart snapshot after successful checkout.
 * Call this from the order success/confirmation page.
 */
export const clearConventionCartSnapshot = () => {
  try {
    sessionStorage.removeItem(ORIGINAL_CART_SNAPSHOT_KEY)
  } catch {
    // Silently fail if sessionStorage not available
  }
}
